import {
  EvidencePack,
  EvidencePackSection,
  EvidencePackSectionStatus,
} from "./EvidencePack";
import { KybDecisionOutcome } from "./Decision";
import { RiskLevel } from "./RiskScore";

export interface EvidencePackGenerationInputs {
  immatriculationVerified: boolean;
  documentsProvided: number;
  documentsRequired: number;
  representativesCount: number;
  shareholdingResolved: boolean;
  screeningHasUnresolved: boolean;
  screeningHasConfirmedHit: boolean;
  riskLevel: RiskLevel;
  compositeScore: number;
  decisionOutcome: KybDecisionOutcome | null;
}

// A minimal but structurally valid single-page PDF, built at call time via
// btoa() rather than hardcoded as a base64 literal (avoids a hand-computed
// encoding mistake). Only meant to make the mock "Exporter" action produce a
// real downloadable .pdf file — the real bytes will come from the backend
// once US-KYB-08-01 ships (no PDF generation endpoint exists yet).
function buildMockPdfDownloadUrl(): string {
  const pdf = [
    "%PDF-1.1",
    "1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj",
    "2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj",
    "3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 3 3]>>endobj",
    "trailer<</Root 1 0 R>>",
  ].join("\n");
  return `data:application/pdf;base64,${btoa(pdf)}`;
}

function addOneYear(isoDate: string): string {
  const date = new Date(isoDate);
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString();
}

// Deterministic stand-in for a real content hash (the real implementation
// will be SHA-256 over the serialized pack, per US-KYB-08-01 Technical Notes
// — WORM + hash-chained, no qualified signature in MVP).
function hashContent(content: string): string {
  let hash = 0;
  for (const char of content) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
  }
  return `sha256:${hash.toString(16).padStart(8, "0")}`;
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function documentsStatus(
  provided: number,
  required: number,
): EvidencePackSectionStatus {
  if (required === 0) {
    return "COMPLETE";
  }
  if (provided >= required) {
    return "COMPLETE";
  }
  return provided > 0 ? "PARTIAL" : "MISSING";
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function screeningStatus(
  hasUnresolved: boolean,
  hasConfirmedHit: boolean,
): { status: EvidencePackSectionStatus; detail: string } {
  if (hasUnresolved) {
    return { status: "PARTIAL", detail: "EN_ATTENTE" };
  }
  if (hasConfirmedHit) {
    return { status: "COMPLETE", detail: "HIT_CONFIRME" };
  }
  return { status: "COMPLETE", detail: "CLEAR" };
}

function buildSections(inputs: EvidencePackGenerationInputs): EvidencePackSection[] {
  const docsStatus = documentsStatus(
    inputs.documentsProvided,
    inputs.documentsRequired,
  );
  const screening = screeningStatus(
    inputs.screeningHasUnresolved,
    inputs.screeningHasConfirmedHit,
  );

  return [
    {
      id: "IDENTIFICATION",
      status: inputs.immatriculationVerified ? "VERIFIED" : "PARTIAL",
      detail: inputs.immatriculationVerified ? "VERIFIE" : "NON_VERIFIE",
    },
    {
      id: "REGISTRATION",
      status: inputs.immatriculationVerified ? "VERIFIED" : "MISSING",
      detail: inputs.immatriculationVerified ? "VERIFIE" : "MANQUANT",
    },
    {
      id: "REPRESENTATION",
      status: inputs.representativesCount > 0 ? "VERIFIED" : "MISSING",
      detail: String(inputs.representativesCount),
    },
    {
      id: "SHAREHOLDING",
      status: inputs.shareholdingResolved ? "COMPLETE" : "PARTIAL",
      detail: inputs.shareholdingResolved ? "COMPLET" : "PARTIEL",
    },
    {
      id: "DOCUMENTS",
      status: docsStatus,
      detail: `${inputs.documentsProvided}/${inputs.documentsRequired}`,
    },
    {
      id: "SCREENING",
      status: screening.status,
      detail: screening.detail,
    },
    {
      id: "RISK_SCORE",
      status: "COMPLETE",
      detail: `${inputs.riskLevel} ${inputs.compositeScore}`,
    },
    {
      id: "DECISION",
      status: inputs.decisionOutcome ? "COMPLETE" : "MISSING",
      detail: inputs.decisionOutcome ?? "EN_ATTENTE",
    },
    {
      id: "RESTRICTIONS",
      status: "COMPLETE",
      detail:
        inputs.decisionOutcome === "APPROVED_WITH_RESTRICTIONS"
          ? "RESTRICTIONS_APPLIQUEES"
          : "AUCUNE",
    },
    {
      id: "AUDIT_TRAIL",
      status: "COMPLETE",
      detail: "10",
    },
  ];
}

/**
 * Deterministic (for fixed inputs) stand-in for a real evidence-pack
 * generation endpoint — no endpoint exists yet, see #25469. Builds the
 * prototype's 10-section structure (`docs/kyb-pm.html`, étape « Evidence
 * Pack »): identification, immatriculation, représentation, actionnariat,
 * documents, screening, score de risque, décision, restrictions, audit
 * trail — plus a content hash for integrity, per US-KYB-08-01.
 */
export function generateMockEvidencePack(
  businessKey: string,
  generatedAt: string,
  inputs: EvidencePackGenerationInputs,
): EvidencePack {
  const datePart = generatedAt.slice(0, 10).replaceAll("-", "");
  const reference = `EP-${businessKey}-${datePart}`;
  const sections = buildSections(inputs);
  return {
    businessKey,
    reference,
    generatedAt,
    nextReviewDate: addOneYear(generatedAt),
    hash: hashContent(reference + JSON.stringify(sections)),
    sections,
    downloadUrl: buildMockPdfDownloadUrl(),
  };
}
